import React, { FC, useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Typography } from "@mui/material";

import styles from "./TopicForm.module.css";

import BackNextButtons from "@/components/BackNextButtons";
import RadioGroupField from "@/components/RadioGroupField/RadioGroupField";
import { IQuizList, SecurityTopicEnum } from "@/pages/SecurityAwareness/types";
import { IDefaultValues } from "@/pages/SecurityAwareness/components/SecurityTopic/components/TopicForm/form/types";
import Results from "@/pages/SecurityAwareness/components/SecurityTopic/components/Results";

interface ITopicForm {
  quizList: Array<IQuizList>;
  quizName: SecurityTopicEnum;
  handleNextTopic?: () => void;
  setTestResults?: React.Dispatch<
    React.SetStateAction<
      Map<string, { score_percent: number; completed_at: string; id: string }>
    >
  >;
}

const TopicForm: FC<ITopicForm> = ({
  quizList,
  quizName,
  handleNextTopic,
  setTestResults,
}) => {
  const [step, setStep] = useState(1);
  const [results, setResults] = useState<Array<string> | null>(null);

  const lastStep = quizList.length;

  const { handleSubmit, control, watch } = useForm<IDefaultValues>({
    defaultValues: {
      [quizName]: {
        step1: "",
        step2: "",
        step3: "",
        step4: "",
        step5: "",
      },
    },
    mode: "onChange",
    reValidateMode: "onBlur",
  });

  const onSubmit = useCallback((data: IDefaultValues) => {
    setResults(Object.values(data[quizName]));
  }, []);

  const fieldName = useMemo(
    () => `${quizName}.${quizList[step - 1].name}`,
    [step]
  );

  const currentValue = watch(fieldName);

  return (
    <form className={styles.form}>
      {results ? (
        <Results
          results={results}
          onResetResults={() => {
            setResults(null);
            setStep(1);
          }}
          handleNextTopic={handleNextTopic}
          quizName={quizName}
          setTestResults={setTestResults}
        />
      ) : (
        <>
          <Typography className={styles.question}>
            Question {step} of {lastStep}
          </Typography>
          <RadioGroupField
            control={control}
            key={fieldName}
            name={fieldName}
            label={quizList[step - 1].question}
            options={quizList[step - 1].options}
            className={styles.radioGroupField}
          />
          <BackNextButtons
            step={step}
            setStep={setStep}
            stepLength={lastStep}
            disabledNext={!currentValue}
            onSubmitFinal={handleSubmit(onSubmit)}
          />
        </>
      )}
    </form>
  );
};

export default TopicForm;
